// src/constants/runbooks.js

/**
 * Diagnostic runbooks: the standing procedure an expert works through for each
 * incident category.
 *
 * These exist so a session does not start from a blank page. The expert can
 * deviate, skip, or add steps — the runbook is a floor on rigour, not a script.
 * Completed steps are recorded as incident events, which is what lets us see
 * later which checks actually find root causes and which are noise.
 *
 * Adding a lane means adding its runbooks here, keyed by the same categoryId
 * used in TAG_CATALOG.
 */

const RUNBOOKS = {
  "stripe-webhooks-events": {
    title: "Webhook delivery triage",
    intent:
      "Establish whether Stripe is sending the events, whether the endpoint is receiving them, and whether the handler is processing them correctly.",
    steps: [
      {
        id: "endpoint-url",
        label: "Confirm the registered endpoint URL matches the live deployment",
        hint: "Stripe Dashboard → Developers → Webhooks. A URL left pointing at a previous deployment is the single most common cause.",
      },
      {
        id: "delivery-attempts",
        label: "Review recent delivery attempts and their HTTP status codes",
        hint: "Distinguish 4xx (endpoint rejected it), 5xx (handler crashed), and timeouts (handler too slow).",
      },
      {
        id: "signing-secret",
        label: "Verify the signing secret in the running environment matches the endpoint",
        hint: "Each endpoint has its own secret. Redeploys and environment promotions routinely leave a stale value behind.",
      },
      {
        id: "mode-mismatch",
        label: "Check for a test/live mode mismatch",
        hint: "Confirm which mode the failing events were sent in and which keys the handler is running with.",
      },
      {
        id: "ack-before-work",
        label: "Confirm the handler acknowledges with 2xx before doing slow work",
        hint: "Stripe retries on timeout. Long synchronous work inside the handler causes duplicate processing.",
      },
      {
        id: "idempotency",
        label: "Verify the handler is safe on redelivery",
        hint: "Stripe guarantees at-least-once delivery. Check the handler keys off event.id or an idempotency record.",
      },
      {
        id: "replay",
        label: "Replay a failed event and observe the handler logs end to end",
        hint: "stripe events resend <event_id>, or trigger locally with the Stripe CLI, and confirm the fix on a real payload.",
      },
    ],
  },

  "subscriptions-billing-logic": {
    title: "Subscription and invoice reconciliation",
    intent:
      "Find where the subscription state in Stripe and the state the application believes diverge, and which one is wrong.",
    steps: [
      {
        id: "identify-objects",
        label: "Pin down the specific customer, subscription, and invoice IDs in scope",
        hint: "Work from concrete object IDs. Reasoning about billing in the abstract wastes session time.",
      },
      {
        id: "compare-state",
        label: "Compare subscription items, quantities, and price IDs against what the app stores",
        hint: "Look for a plan change the application recorded but Stripe did not, or the reverse.",
      },
      {
        id: "proration",
        label: "Check proration behaviour and the billing cycle anchor on the last change",
        hint: "proration_behavior and billing_cycle_anchor explain most 'wrong amount' reports.",
      },
      {
        id: "invoice-lifecycle",
        label: "Trace the invoice through its status transitions",
        hint: "draft → open → paid / uncollectible / void. Identify where it stopped and why.",
      },
      {
        id: "retry-dunning",
        label: "Review retry and dunning settings and the last payment attempt outcome",
        hint: "Smart Retries and dunning schedules change when a subscription actually lapses.",
      },
      {
        id: "entitlement-source",
        label: "Confirm entitlements are driven by invoice payment, not checkout completion alone",
        hint: "Granting access on checkout.session.completed leaves the app wrong whenever payment later fails.",
      },
    ],
  },

  "checkout-payments-access": {
    title: "Payment and access-grant triage",
    intent:
      "Determine whether the payment failed, or whether the payment succeeded and the entitlement never landed — these have completely different fixes.",
    steps: [
      {
        id: "reproduce",
        label: "Reproduce against a specific PaymentIntent or Checkout Session",
        hint: "Get one failing ID from the customer before theorising.",
      },
      {
        id: "decline-reason",
        label: "Read last_payment_error and the decline code",
        hint: "Issuer declines, fraud blocks, and integration errors look identical to the end user and are not the same problem.",
      },
      {
        id: "sca",
        label: "Establish whether SCA / 3DS was required and whether the flow completed it",
        hint: "requires_action left unhandled is a silent conversion killer on European traffic.",
      },
      {
        id: "paid-no-access",
        label: "Check for payment succeeded but access not granted",
        hint: "Usually a webhook that never arrived, or a redirect-based grant that the customer never returned to.",
      },
      {
        id: "return-url",
        label: "Verify the success/return URL does not grant access by itself",
        hint: "A return URL is not proof of payment. Anyone can visit it.",
      },
      {
        id: "duplicates",
        label: "Check for duplicate charges from double submission or client retries",
        hint: "Confirm the create call passes an idempotency key.",
      },
    ],
  },

  "connect-payouts-reliability": {
    title: "Connect account and payout triage",
    intent:
      "Establish whether money is blocked by account requirements, by schedule, or by a genuine transfer failure.",
    steps: [
      {
        id: "account-state",
        label: "Read the connected account's charges_enabled, payouts_enabled, and details_submitted",
        hint: "These three booleans answer most 'why has nothing paid out' questions immediately.",
      },
      {
        id: "requirements",
        label: "Check outstanding requirements and any past-due verification",
        hint: "requirements.currently_due and past_due block payouts without an obvious error anywhere else.",
      },
      {
        id: "balance",
        label: "Compare available balance against pending balance and the payout schedule",
        hint: "Funds still in the pending window are not late, they are simply not available yet.",
      },
      {
        id: "trace-transfer",
        label: "Trace the transfer and its balance transactions for the amount in dispute",
        hint: "Follow the money object by object rather than reasoning from dashboard totals.",
      },
      {
        id: "failure-codes",
        label: "Check failed transfers and payouts for their failure codes",
        hint: "Bank rejections and account closures surface here, not in the charge.",
      },
      {
        id: "onboarding-gap",
        label: "Confirm onboarding was genuinely completed rather than abandoned mid-flow",
        hint: "Interrupted Connect onboarding leaves accounts that look created but cannot receive funds.",
      },
    ],
  },
};

/** Fallback used when an incident has no category, or a category with no runbook. */
const GENERAL_RUNBOOK = {
  title: "General incident triage",
  intent:
    "Establish reproducible symptoms and a concrete failing object before changing anything.",
  steps: [
    {
      id: "symptoms",
      label: "Establish exactly what the customer observes, and when it started",
    },
    {
      id: "reproduce",
      label: "Reproduce the failure, or capture evidence of it happening",
    },
    {
      id: "recent-change",
      label: "Identify what changed recently — deploys, config, key rotation, plan changes",
    },
    {
      id: "isolate",
      label: "Narrow the failure to a single component or request path",
    },
    {
      id: "verify",
      label: "Apply the fix and verify it against the original failing case",
    },
  ],
};

export function getRunbookForCategory(categoryId) {
  const runbook = RUNBOOKS[categoryId];
  if (!runbook) return { ...GENERAL_RUNBOOK, categoryId: null };
  return { ...runbook, categoryId };
}

export function getRunbookStep(categoryId, stepId) {
  return (
    getRunbookForCategory(categoryId).steps.find((s) => s.id === stepId) || null
  );
}
